import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import CinematicHero from "../components/home/CinematicHero";
import WordingDivider from "../components/rj-components/WordingDivider";
import SimpleEventCard from "../components/cards/SimpleEventCard";
import AboutSection from "../components/AboutSection";
import EventCalendar, { eventDate } from "../components/EventCalendar";
import GlobalContext from "../contexts/store";

import unlockVegasStage from "../assets/images/headers/unlock-vegas-stage.png";

/** One card per organization — same grouping as upcomingEvents on main. */
function uniqueByOrganization(list) {
  const seen = new Set();
  return (list || []).filter((ev) => {
    const key =
      ev.organization?._id ||
      ev.organization?.slug ||
      (ev.organization?.name || ev.name || ev.slug || ev.key || "")
        .toString()
        .trim()
        .toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function normalizeLive(ev) {
  return {
    key: ev._id || ev.slug,
    _id: ev._id,
    slug: ev.slug,
    name: ev.name,
    image:
      ev?.promo?.story ||
      ev?.promo?.post ||
      ev?.promo?.banner ||
      ev?.image,
    location: ev?.organization?.location_name || ev?.organization?.name,
    start_date_local: ev.start_date_local || ev.start_date,
    start_date: ev.start_date,
    start_time_display: ev.start_time_display,
    timezone_abbr: ev.timezone_abbr,
    promo: ev.promo,
    organization: ev.organization,
    to: `/events/event/${ev.slug}`,
  };
}

function cardTitle(show) {
  return show.organization?.name || show.name;
}

function Landing() {
  const { events, upcomingEvents } = useContext(GlobalContext);
  const [query, setQuery] = useState("");
  const [selectedKey, setSelectedKey] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const liveDates = useMemo(() => {
    const base = (events && events.length ? events : upcomingEvents) || [];
    return base
      .filter((e) => e?.slug && !isNaN(eventDate(e)))
      .map(normalizeLive);
  }, [events, upcomingEvents]);

  const liveOrgCards = useMemo(() => {
    const fromUpcoming =
      upcomingEvents && upcomingEvents.length
        ? upcomingEvents
            .filter((e) => e?.slug && !isNaN(eventDate(e)))
            .map(normalizeLive)
        : [];
    return uniqueByOrganization(
      fromUpcoming.length ? fromUpcoming : liveDates
    );
  }, [upcomingEvents, liveDates]);

  const filteredShows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return uniqueByOrganization(
      liveOrgCards.filter((show) => {
        const label = `${show.name || ""} ${show.organization?.name || ""}`;
        return !q || label.toLowerCase().includes(q);
      })
    );
  }, [liveOrgCards, query]);

  const clearSearch = () => {
    setQuery("");
  };

  return (
    <div className="bg-ink">
      <CinematicHero />

      <WordingDivider text={"Upcoming Shows"} />

      <div className="mx-auto w-full max-w-[1200px] px-4 sm:px-8">
        {/* Search */}
        <div className="relative mx-auto mb-6 max-w-lg">
          <label htmlFor="show-search" className="sr-only">
            Search shows by name
          </label>
          <svg
            viewBox="0 0 24 24"
            className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-white/40"
            fill="none"
            aria-hidden
          >
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.6" />
            <path
              d="M20 20l-3.5-3.5"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
          <input
            id="show-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search shows by name…"
            className="h-12 w-full rounded-lg border-[0.5px] border-white/10 bg-white/[0.04] pl-12 pr-4 text-[15px] text-white placeholder:text-white/40 outline-none transition focus:border-brand-blue focus:ring-1 focus:ring-brand-blue"
          />
        </div>

        {/* Interactive calendar + upcoming rail */}
        <EventCalendar
          events={
            query.trim()
              ? liveDates.filter((s) =>
                  `${s.name || ""} ${s.organization?.name || ""}`
                    .toLowerCase()
                    .includes(query.trim().toLowerCase())
                )
              : liveDates
          }
          selectedKey={selectedKey}
          onSelectDay={setSelectedKey}
          className="mb-8"
        />

        {query && (
          <div className="mb-5 flex flex-wrap items-center gap-3 text-sm text-white/60">
            <span>
              Poster grid · {filteredShows.length}{" "}
              {filteredShows.length === 1 ? "show" : "shows"} matching “
              {query.trim()}”
            </span>
            <button
              type="button"
              onClick={clearSearch}
              className="font-semibold text-brand-blue-bright hover:underline"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Mobile: 2-up grid; lg+: 3-up row */}
      <div className="mx-auto grid w-full max-w-[1200px] grid-cols-2 gap-x-4 gap-y-8 px-4 pb-10 sm:gap-x-6 sm:gap-y-12 sm:px-8 lg:grid-cols-4 lg:gap-x-8">
        {filteredShows.length > 0 ? (
          filteredShows.map((show) => (
            <Link
              key={show.key || show._id || show.slug}
              to={show.to || `/events/event/${show.slug}`}
              className="block min-w-0 no-underline text-inherit"
            >
              <SimpleEventCard
                image={show.image}
                title={cardTitle(show)}
                location={show.location}
              />
            </Link>
          ))
        ) : (
          <p className="col-span-2 py-8 text-center text-white/50 lg:col-span-4">
            {query.trim()
              ? "No shows match your search."
              : "No upcoming shows are listed right now. Check back soon."}{" "}
            {query.trim() ? (
              <button
                type="button"
                onClick={clearSearch}
                className="font-semibold text-brand-blue-bright hover:underline"
              >
                Clear filters
              </button>
            ) : null}
          </p>
        )}
      </div>

      <WordingDivider text={"Unlock Vegas Entertainment"} />

      <div className="relative w-full overflow-hidden">
        <img
          src={unlockVegasStage}
          alt="Theater seats facing a stage lit with blue spotlights"
          className="h-[42vh] w-full object-cover object-center sm:h-[48vh] md:h-[56vh]"
        />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(10,10,10,0.35) 0%, transparent 28%, transparent 62%, rgba(10,10,10,0.85) 100%)",
          }}
        />
      </div>

      <AboutSection />
    </div>
  );
}

export default Landing;
